import {Dispatch, FormEvent, ReactNode, SetStateAction, useState} from 'react';
import {ApiError, useBackendClient} from '@legit.health/backend-client';
import {FILE_MAX_SIZE, generatePath, UserId} from '@legit.health/ui';
import SendIcon from '@mui/icons-material/Send';
import {Box, Button, Typography} from '@mui/material';
import {useMutation} from '@tanstack/react-query';
import {TFunction} from 'i18next';
import ImageField from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/FollowUpForm/components/ImageField';
import MutationStatusIndicator from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/FollowUpForm/components/MutationStatusIndicator';
import BottomBar from '@/shared/components/diagnosticReport/DiagnosticReportForm/steps/BottomBar';
import {AnamnesisQuestionsValue} from '@/shared/components/diagnosticReport/DiagnosticReportForm/types';
import {processAnamnesisQuestions} from '@/shared/components/diagnosticReport/DiagnosticReportForm/utils/mapFormValuesToApiFormat';
import useImageMaxFileSize from '@/shared/hooks/useImageMaxFileSize';
import {EncounterId} from '@/shared/interfaces/Encounter';
import UploadingImagesOverlay from './components/UploadingImagesOverlay';
import {BODY_PARTS, BODY_PARTS_ARRAY} from './consts';
import useImageValidation from './hooks/useImageValidation';
import {FormValues} from './interfaces';
import createInitialFormState from './utils/createInitialFormState';
import mapFormValuesToApiFormat from './utils/mapFormValuesToApiFormat';

const PRECEDING_STEPS_COUNT = 2;

export default function ApasiGlobalForm({
  patientId,
  setCurrentStep,
  t,
  steps,
  onSuccess,
  anamnesisAnswers,
  visitIdentifier,
  isMobileOnlyPhotoEnabled = false
}: {
  patientId: UserId;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  t: TFunction;
  steps: {label: string; icon: ReactNode}[];
  onSuccess: (encounterId: EncounterId) => void;
  anamnesisAnswers: AnamnesisQuestionsValue;
  visitIdentifier: string;
  isMobileOnlyPhotoEnabled?: boolean;
}) {
  const customMaxFileSize = useImageMaxFileSize();
  const sizeErrorMessage = t('ui:dropzone.error.fileSize', {
    maxSize: `${(customMaxFileSize || FILE_MAX_SIZE) / 1000000} MB`
  });
  const generalErrorMessage = t('ui:dropzone.error.general');
  const typeErrorMessage = t('ui:dropzone.error.type');

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [compressionError, setCompressionError] = useState<string | null>(null);
  const [currentBodyPart, setCurrentBodyPart] = useState(0);
  const [form, setForm] = useState<FormValues>(createInitialFormState);

  const {imageValidationError, isValidating, handleImageChange, validateImage} =
    useImageValidation(t);

  const backendClient = useBackendClient();

  const {mutate, status: mutationStatus} = useMutation({
    mutationFn: async function (values: FormValues) {
      const bodyPartImages = await mapFormValuesToApiFormat(values);
      return await backendClient.sendRequest<{
        encounterId: EncounterId;
      }>({
        method: 'POST',
        path: generatePath('/apasi-global'),
        body: {
          patient: patientId,
          bodyPartImages,
          anamnesisQuestions: processAnamnesisQuestions(anamnesisAnswers),
          visitIdentifier
        }
      });
    }
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    mutate(form, {
      onSuccess: function (response) {
        try {
          onSuccess(response.data.encounterId);
        } catch {
          setSubmitError('An unexpected error occurred after submission.');
        }
      },
      onError: function (error) {
        if (error instanceof ApiError) {
          setSubmitError(error.message);
        }
      }
    });
  }

  async function handleNext() {
    const bodyPartCode = BODY_PARTS_ARRAY[currentBodyPart];

    if (!bodyPartCode) {
      return;
    }

    const isValid = await validateImage(BODY_PARTS[bodyPartCode], form, setForm);
    if (isValid && currentBodyPart < BODY_PARTS_ARRAY.length - 1) {
      setCurrentBodyPart((prev) => prev + 1);
    }
  }

  function handleBack() {
    if (currentBodyPart > 0) {
      setCurrentBodyPart((prev) => prev - 1);
    } else {
      setCurrentStep((s) => s - 1);
    }
  }

  const currentBodyPartCode = BODY_PARTS_ARRAY[currentBodyPart];
  if (!currentBodyPartCode) {
    throw new Error('Invalid body part code');
  }
  const isLastBodySite = currentBodyPart === BODY_PARTS_ARRAY.length - 1;
  const currentImageHasFile = form[currentBodyPartCode].file !== null;
  const isMutationInProgressOrSuccess =
    mutationStatus === 'pending' || mutationStatus === 'success';
  const isSubmitting = isMutationInProgressOrSuccess || isValidating;

  const getViewType = (bodyPartCode: string): 'front' | 'back' | 'closeUp' => {
    if (bodyPartCode.startsWith('closeUp')) {
      return 'closeUp';
    }
    if (bodyPartCode.includes('Front')) {
      return 'front';
    }
    return 'back';
  };

  const getBodySiteGroupKey = (bodyPartCode: string): string => {
    const closeUpMapping: Record<string, string> = {
      closeUpHead: 'head',
      closeUpTrunk: 'trunk',
      closeUpArms: 'arms',
      closeUpLegs: 'legs'
    };
    return closeUpMapping[bodyPartCode] || bodyPartCode;
  };

  const currentViewType = getViewType(currentBodyPartCode);

  const bodyPartForDisplay =
    currentViewType === 'closeUp'
      ? t(`ui:apasiGlobal.representative.${getBodySiteGroupKey(currentBodyPartCode)}`)
      : t(`ui:apasiGlobal.bodySites.${currentBodyPartCode}`);

  return (
    <form onSubmit={handleSubmit}>
      <UploadingImagesOverlay isOpen={mutationStatus === 'pending'} t={t} />
      <Typography
        sx={{
          textAlign: 'center',
          mt: 4,
          mb: {xs: 0, md: 2}
        }}
      >
        {t('ui:apasiGlobal.instructions')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 2,
          maxWidth: 400,
          mx: 'auto'
        }}
      >
        <ImageField
          key={currentBodyPartCode}
          value={form[currentBodyPartCode]}
          placeholderImage={BODY_PARTS[currentBodyPartCode].image}
          label={''}
          name={currentBodyPartCode}
          sizeErrorMessage={sizeErrorMessage}
          typeErrorMessage={typeErrorMessage}
          generalErrorMessage={generalErrorMessage}
          onChange={function (imageWithCrop) {
            setCompressionError(null);
            handleImageChange(currentBodyPartCode, imageWithCrop, setForm);
          }}
          onCompressionError={setCompressionError}
          dataTestId={currentBodyPartCode}
          allowGallery={!isMobileOnlyPhotoEnabled}
        />
        {(imageValidationError ?? compressionError) && (
          <MutationStatusIndicator
            mutationStatus="error"
            error={(imageValidationError ?? compressionError) as string}
            t={t}
          />
        )}
      </Box>
      <Typography variant="h3" sx={{textAlign: 'center', color: 'text.secondary'}}>
        {t(`ui:apasiGlobal.bodySites.${currentBodyPartCode}`)}
      </Typography>
      <Typography variant="body1" sx={{textAlign: 'center', mb: 2, color: 'text.secondary'}}>
        {t(`ui:apasiGlobal.perspectives.${currentViewType}`, {
          bodySite: bodyPartForDisplay
        })}
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          mb: 2,
          color: 'text.secondary'
        }}
      >
        {currentBodyPart + 1} / {BODY_PARTS_ARRAY.length}
      </Typography>
      {submitError && <MutationStatusIndicator mutationStatus="error" error={submitError} t={t} />}
      <BottomBar
        activeStep={PRECEDING_STEPS_COUNT + currentBodyPart}
        isBackEnabled={!isValidating}
        isNextEnabled={currentImageHasFile && !isValidating}
        onBack={handleBack}
        onNext={handleNext}
        t={t}
        totalSteps={steps.length + BODY_PARTS_ARRAY.length - 1}
        rightButton={
          isLastBodySite && currentImageHasFile ? (
            <Button
              color="secondary"
              disableElevation
              size="medium"
              variant="contained"
              endIcon={<SendIcon />}
              type="submit"
              data-testid="stepperNextButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('ui:sending') : t('ui:send')}
            </Button>
          ) : undefined
        }
      />
    </form>
  );
}
